import React, { useEffect, useState } from "react";

// The live parts of the page that come from the clock: NSE open or closed, and the next 6 pm refresh.
// India keeps no daylight saving, so IST is UTC + 5:30 all year. Holidays are NSE's own trading-holiday list
// (public/bi-data/nse-holidays.json, fetched from NSE's holiday-master on 12 Oct 2026, 2026 only). For a year
// the list does not cover, no next-open date is named. Muhurat (special Diwali evening) sessions are not modelled.

const IST = 330 * 60000;
const OPEN = 9 * 60 + 15;
const CLOSE = 15 * 60 + 30;
const REFRESH = 18 * 60;
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// IST calendar fields of a moment.
export const ist = (now) => {
  const d = new Date(now + IST);
  return { iso: d.toISOString().slice(0, 10), dow: d.getUTCDay(), min: d.getUTCHours() * 60 + d.getUTCMinutes(), day: d.getUTCDate(), mon: d.getUTCMonth() };
};
const addDays = (now, n) => now + n * 86400000;
const dayLabel = (p) => `${DAY[p.dow]} ${p.day} ${MON[p.mon]}`;

// "2 h 14 min", "45 min", "1 day 3 h"
export const span = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} min`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;
  const d = Math.floor(m / (24 * 60));
  const h = Math.floor((m % (24 * 60)) / 60);
  return `${d} day${d === 1 ? "" : "s"}${h ? ` ${h} h` : ""}`;
};

// cal = { years: ["2026"], holidays: [{date, name}] } or null while it loads.
export function marketStatus(now, cal) {
  const known = (iso) => !!cal && cal.years.includes(iso.slice(0, 4));
  const holidayOf = (iso) => (cal ? (cal.holidays.find((h) => h.date === iso) || {}).name || "" : "");
  const trading = (p) => p.dow >= 1 && p.dow <= 5 && !holidayOf(p.iso);
  const t = ist(now);
  // a year the holiday list does not cover: say the hours, never open or closed
  if (!known(t.iso)) return { open: null, head: "NSE hours", sub: "9:15 am to 3:30 pm IST, Mon to Fri" };
  if (trading(t) && t.min >= OPEN && t.min < CLOSE)
    return { open: true, head: "NSE open", sub: `closes 3:30 pm · in ${span(CLOSE - t.min)}` };
  const head = holidayOf(t.iso) ? `NSE holiday: ${holidayOf(t.iso)}` : "NSE closed";
  // the next session start
  for (let k = 0; k <= 12; k += 1) {
    const p = ist(addDays(now, k));
    if (!known(p.iso)) return { open: false, head, sub: "opens 9:15 am on the next trading day" };
    if (trading(p) && (k > 0 || t.min < OPEN)) {
      const wait = k * 24 * 60 + OPEN - t.min;
      return { open: false, head, sub: `opens 9:15 am ${k === 0 ? "today" : k === 1 ? "tomorrow" : dayLabel(p)} · in ${span(wait)}` };
    }
  }
  return { open: false, head, sub: "hours 9:15 am to 3:30 pm IST, Mon to Fri" };
}

// Minutes to the next 6 pm IST: the refresh after which an opened score costs credits again.
export const toRefresh = (now) => {
  const t = ist(now);
  return t.min < REFRESH ? REFRESH - t.min : 24 * 60 - t.min + REFRESH;
};

// The time, ticking every `every` ms while the tab is visible.
export function useNow(every = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id = null;
    const start = () => {
      if (id === null && !document.hidden) id = setInterval(() => setNow(Date.now()), every);
    };
    const stop = () => {
      if (id !== null) clearInterval(id);
      id = null;
    };
    const vis = () => {
      if (document.hidden) stop();
      else {
        setNow(Date.now());
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", vis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", vis);
    };
  }, [every]);
  return now;
}

// The NSE holiday list, fetched once per page load and shared by every clock on the page. Resolves to the
// list, or to null when it cannot be had: no answer within 10 s, a non-ok answer, bad JSON or a file without
// `years` and `holidays`. A failure is not kept, so the next caller asks again instead of reusing it.
const CAL_URL = "/bi-data/nse-holidays.json";
const CAL_TIMEOUT_MS = 10000;
let calLoading = null;
let calLoaded = null; // the list once it has arrived, so a later clock starts with it and shows no skeleton

export const loadCalendar = () => {
  if (calLoaded) return Promise.resolve(calLoaded);
  if (!calLoading) {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timer = null;
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => {
        if (ctrl) ctrl.abort();
        resolve(null);
      }, CAL_TIMEOUT_MS);
    });
    const asked = Promise.resolve()
      .then(() => fetch(CAL_URL, ctrl ? { signal: ctrl.signal } : undefined))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((d) => (d && Array.isArray(d.holidays) && Array.isArray(d.years) ? d : null));
    const p = Promise.race([asked, timedOut])
      .catch(() => null)
      .then((d) => {
        clearTimeout(timer);
        if (d) calLoaded = d;
        if (calLoading === p) calLoading = null;
        return d;
      });
    calLoading = p;
  }
  return calLoading;
};

// Forget the list (tests start each case from a page that has not loaded it yet).
export const resetCalendar = () => {
  calLoading = null;
  calLoaded = null;
};

// The holiday list with its load state, so a page can show a skeleton first and then the hours:
// { status: "loading" | "ok" | "failed" | "skipped", cal }. skip: never load (the list stays "skipped").
export function useCalendarState({ skip = false } = {}) {
  const [state, setState] = useState(() => {
    if (skip) return { status: "skipped", cal: null };
    return calLoaded ? { status: "ok", cal: calLoaded } : { status: "loading", cal: null };
  });
  useEffect(() => {
    if (skip) {
      setState((s) => (s.status === "skipped" ? s : { status: "skipped", cal: null }));
      return undefined;
    }
    let gone = false;
    loadCalendar().then((c) => {
      if (gone) return;
      setState((s) => {
        if (c) return s.status === "ok" && s.cal === c ? s : { status: "ok", cal: c };
        return s.status === "failed" ? s : { status: "failed", cal: null };
      });
    });
    return () => {
      gone = true;
    };
  }, [skip]);
  return state;
}

// The holiday list, or null while it loads and when it failed.
export function useCalendar() {
  return useCalendarState().cal;
}

// "NSE closed · opens 9:15 am Tue 13 Oct · in 13 h 5 min", with a dot that turns green while the market is open.
// Classes are named from cls ("fn": the foundation's .fn-mclock); className adds modifiers (.fn-mclock-ping,
// .fn-mclock-pill).
// While the holiday list loads it shows `loading` (nothing by default). When the list cannot be had it shows
// nothing, or with hoursOnFail the hours instead ("NSE hours · 9:15 am to 3:30 pm IST, Mon to Fri"), since
// without the holidays it cannot say open or closed.
export function MarketClock({ cls = "bx", className = "", loading = null, hoursOnFail = false }) {
  const now = useNow(30000);
  const { status, cal } = useCalendarState();
  if (status === "loading") return loading;
  if (!cal && !hoursOnFail) return null;
  const s = marketStatus(now, cal);
  return (
    <span className={`${cls}-mclock` + (className ? ` ${className}` : "") + (s.open ? " is-open" : "")}>
      <span aria-hidden="true" className={`${cls}-mclock-dot`} />
      <strong>{s.head}</strong>
      <span>{s.sub}</span>
    </span>
  );
}
